import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiUser, FiPhone, FiMail, FiMapPin, FiCalendar, FiCamera, FiTrash2, FiUpload } from 'react-icons/fi';
import Loader from '../../utils/loader';
import toast from 'react-hot-toast';
import validator from 'validator';
import api from '../../libs/axios';

enum UserStatus {
    PENDING = 'pending',
    VERIFIED = 'verified',
    SUSPENDED = 'suspended',
    INITIATED = 'initiated',
    // DEACTIVATED = 'deactivated'
}

interface EditUserOffcanvasProps {
    user: User | null
    fetchUser: () => void;
    showEditUserCanvas: boolean;
    setShowEditUserCanvas: (show: boolean) => void;
}

interface UserFormData {
    id?: string;
    phone_number: string;
    status: UserStatus;
    first_name: string;
    last_name: string;
    email: string;
    role_id: string | number;
    date_of_birth: string;
    gender: string;
    address: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    profile_picture: File | null;
}

interface UserDetailsAttributes {
    first_name?: string;
    last_name?: string;
    email?: string;
    date_of_birth?: Date;
    gender?: string;
    profile_picture?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    pincode?: string;
    created_at?: Date;
    updated_at?: Date;
}

interface User {
    role_id: string | number;
    id: string;
    phone_number: string;
    status: UserStatus;
    created_at?: Date;
    updated_at?: Date;
    details?: UserDetailsAttributes;
}

const EditUserOffcanvas: React.FC<EditUserOffcanvasProps> = ({
    user,
    showEditUserCanvas,
    fetchUser,
    setShowEditUserCanvas
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [roles, setRoles] = useState<any[]>([]);
    const [formData, setFormData] = useState<UserFormData>({
        phone_number: '',
        role_id: '',
        status: UserStatus.PENDING,
        first_name: '',
        last_name: '',
        email: '',
        date_of_birth: '',
        gender: '',
        address: '',
        city: '',
        state: '',
        country: '',
        pincode: '',
        profile_picture: null
    });

    const [errors, setErrors] = useState<Partial<UserFormData>>({});
    const [loading, setLoading] = useState(false);
    const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
    const [uploadedProfilePictureUrl, setUploadedProfilePictureUrl] = useState<string | null>(null);

    const fetchRoles = async () => {
        try {
            const response = await api.get(`/roles/active`);
            setRoles(response.data);
        } catch (error: any) {
            console.error("Error fetching roles:", error);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [])

    useEffect(() => {
        if (user) {
            setFormData({
                id: user.id,
                phone_number: user.phone_number || '',
                status: user.status || UserStatus.PENDING,
                first_name: user.details?.first_name || '',
                last_name: user.details?.last_name || '',
                email: user.details?.email || '',
                role_id: user?.role_id || '',
                date_of_birth: user.details?.date_of_birth
                    ? new Date(user.details.date_of_birth).toISOString().split('T')[0]
                    : '',
                gender: user.details?.gender || '',
                address: user.details?.address || '',
                city: user.details?.city || '',
                state: user.details?.state || '',
                country: user.details?.country || '',
                pincode: user.details?.pincode || '',
                profile_picture: null
            });
            setProfilePicturePreview(user.details?.profile_picture || null);
            setUploadedProfilePictureUrl(user.details?.profile_picture || null);
        }
    }, [user]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name as keyof UserFormData]) {
            setErrors(prev => ({
                ...prev,
                [name]: undefined
            }));
        }
    };

    // Handle profile picture upload
    const handleProfilePictureUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select a valid image file');
                return;
            }

            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                toast.error('Image size should be less than 2MB');
                return;
            }

            setFormData(prev => ({
                ...prev,
                profile_picture: file
            }));

            // Create preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfilePicturePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    // Upload profile picture file to server
    const uploadProfilePictureFile = async (file: File): Promise<string> => {
        const uploadFormData = new FormData();
        uploadFormData.append('type', 'profile_pictures');
        uploadFormData.append('files', file);

        try {
            const response = await api.post('/media/upload', uploadFormData);
            const uploadedFiles = response.data?.files || [];

            if (!uploadedFiles.length) {
                throw new Error('Failed to upload profile picture file');
            }

            return uploadedFiles[0].url;
        } catch (error: any) {
            throw new Error(error?.response?.data?.message || 'Failed to upload profile picture file');
        }
    };

    // Remove profile picture
    const handleRemoveProfilePicture = () => {
        setFormData(prev => ({
            ...prev,
            profile_picture: null
        }));
        setProfilePicturePreview(user?.details?.profile_picture || null);
        setUploadedProfilePictureUrl(user?.details?.profile_picture || null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<UserFormData> = {};

        // First Name validation (required, only letters and spaces)
        if (!formData.first_name.trim()) {
            newErrors.first_name = 'First name is required';
        } else if (!validator.isAlpha(formData.first_name.replace(/\s/g, ''))) {
            newErrors.first_name = 'First name should contain only letters';
        } else if (formData.first_name.trim().length < 2) {
            newErrors.first_name = 'First name must be at least 2 characters';
        } else if (formData.first_name.trim().length > 50) {
            newErrors.first_name = 'First name must be less than or equal to 50 characters';
        }

        // Last Name validation (required, only letters and spaces)
        if (!formData.last_name.trim()) {
            newErrors.last_name = 'Last name is required';
        } else if (!validator.isAlpha(formData.last_name.replace(/\s/g, ''))) {
            newErrors.last_name = 'Last name should contain only letters';
        } else if (formData.last_name.trim().length < 2) {
            newErrors.last_name = 'Last name must be at least 2 characters';
        } else if (formData.last_name.trim().length > 50) {
            newErrors.last_name = 'Last name must be less than or equal to 50 characters';
        }

        // Email validation (required, must be valid)
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!validator.isEmail(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Phone Number validation (required, must be valid mobile)
        if (!formData.phone_number.trim()) {
            newErrors.phone_number = 'Phone number is required';
        } else {
            const phoneWithoutCountryCode = formData.phone_number.replace(/^\+91/, '');
            if (!validator.isMobilePhone(phoneWithoutCountryCode, 'en-IN')) {
                newErrors.phone_number = 'Please enter a valid mobile number';
            }
        }

        // Date of Birth validation (if provided, must be valid and not future)
        if (formData.date_of_birth) {
            const today = new Date();
            const minDate = new Date(today.getFullYear() - 13, today.getMonth(), today.getDate());

            if (!validator.isDate(formData.date_of_birth)) {
                newErrors.date_of_birth = 'Please enter a valid date';
            } else if (new Date(formData.date_of_birth) > today) {
                newErrors.date_of_birth = 'Date of birth cannot be in the future';
            } else if (new Date(formData.date_of_birth) > minDate) {
                newErrors.date_of_birth = 'User must be at least 13 years old';
            }
        }

        // Pincode validation (if provided, must be valid pincode)
        if (formData.pincode && !validator.isPostalCode(formData.pincode, 'any')) {
            newErrors.pincode = 'Please enter a valid pincode (6 digits)';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setLoading(true);
            try {
                let profilePictureUrl = uploadedProfilePictureUrl;

                // Upload new profile picture file if selected
                if (formData.profile_picture) {
                    toast.loading('Uploading profile picture...');
                    profilePictureUrl = await uploadProfilePictureFile(formData.profile_picture);
                    toast.dismiss();
                }

                // Prepare the JSON payload
                const jsonPayload = {
                    phone_number: formData.phone_number,
                    role_id: formData.role_id,
                    status: formData.status,
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                    date_of_birth: formData.date_of_birth,
                    gender: formData.gender,
                    address: formData.address,
                    city: formData.city,
                    state: formData.state,
                    country: formData.country,
                    pincode: formData.pincode,
                    profile_picture: profilePictureUrl
                };

                await api.put(`/users/${user?.id}`, jsonPayload);
                toast.success('User updated successfully');
                setErrors({});
                fetchUser();
                setShowEditUserCanvas(false);
            } catch (error: any) {
                toast.dismiss();
                // Handle different types of errors
                const errorMessage = error?.response?.data?.message || error.message || 'Failed to update user';
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        } else {
            toast.error('Please fix the validation errors before submitting');
        }
    };

    const handleClose = () => {
        setShowEditUserCanvas(false);
        setErrors({});
        setFormData(prev => ({
            ...prev,
            profile_picture: null
        }));
        setProfilePicturePreview(user?.details?.profile_picture || null);
        setUploadedProfilePictureUrl(user?.details?.profile_picture || null);
    };

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${showEditUserCanvas ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${showEditUserCanvas ? 'translate-x-0' : 'translate-x-full'
                }`}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiUser className="mr-2" />
                        Edit User
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Loading State */}
                {loading && (
                    <Loader />
                )}

                {/* Form */}
                {!loading && (
                    <div className="flex flex-col h-full">
                        <div className="flex-1 overflow-y-auto p-6 space-y-6">

                            {/* Profile Picture Upload */}
                            <div className="flex flex-col items-center mb-8">
                                <div className="relative mb-4">
                                    {profilePicturePreview ? (
                                        <div className="relative">
                                            <img
                                                src={profilePicturePreview}
                                                alt="Profile"
                                                className="w-24 h-24 rounded-full object-cover border-4 border-primary-100"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleRemoveProfilePicture}
                                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="w-24 h-24 rounded-full bg-primary-100 border-4 border-primary-200 flex items-center justify-center">
                                            <FiUser className="w-8 h-8 text-primary-400" />
                                        </div>
                                    )}
                                </div>

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleProfilePictureUpload}
                                    className="hidden"
                                />

                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={loading}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                                >
                                    <FiUpload className="w-4 h-4" />
                                    {profilePicturePreview ? 'Change Picture' : 'Upload Profile Picture'}
                                </button>
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                    PNG, JPG up to 2MB
                                </p>
                            </div>

                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            First Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.first_name ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter first name"
                                        />
                                        {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Last Name *
                                        </label>
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.last_name ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter last name"
                                        />
                                        {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <FiMail className="inline mr-1" />
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        placeholder="Enter email address"
                                    />
                                    {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <FiPhone className="inline mr-1" />
                                        Phone Number *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="tel"
                                            name="phone_number"
                                            maxLength={10}
                                            value={formData.phone_number}
                                            onChange={(e) => {
                                                const regx = /^[0-9]*$/;
                                                if (!regx.test(e.target.value)) return;
                                                handleInputChange(e);
                                            }}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.phone_number ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter phone number"
                                        />
                                    </div>
                                    {errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FiCalendar className="inline mr-1" />
                                            Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            name="date_of_birth"
                                            value={formData.date_of_birth}
                                            onChange={handleInputChange}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.date_of_birth ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            max={new Date().toISOString().split('T')[0]}
                                        />
                                        {errors.date_of_birth && <p className="text-red-500 text-xs mt-1">{errors.date_of_birth}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Gender
                                        </label>
                                        <select
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="Male">Male</option>
                                            <option value="Female">Female</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>

                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Role
                                        </label>
                                        <select
                                            name="role_id"
                                            value={formData.role_id}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border capitalize border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {roles.map((role) => (
                                                <option key={role.id} value={role.id} className="capitalize">
                                                    {role.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Status
                                        </label>
                                        <select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            <option value={UserStatus.PENDING}>Pending</option>
                                            <option value={UserStatus.VERIFIED}>Verified</option>
                                            <option value={UserStatus.SUSPENDED}>Suspended</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Address Information */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
                                    <FiMapPin className="inline mr-1" />
                                    Address Information
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Address
                                    </label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        placeholder="Enter full address"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            City
                                        </label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Enter city"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            State
                                        </label>
                                        <input
                                            type="text"
                                            name="state"
                                            value={formData.state}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Enter state"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Country
                                        </label>
                                        <input
                                            type="text"
                                            name="country"
                                            value={formData.country}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            placeholder="Enter country"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Pincode
                                        </label>
                                        <input
                                            type="text"
                                            name="pincode"
                                            value={formData.pincode}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (!val || /^\d+$/.test(val)) {
                                                    handleInputChange(e);
                                                }
                                            }}
                                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.pincode ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="Enter pincode"
                                            maxLength={6}
                                        />
                                        {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="border-t border-gray-200 p-6 mb-20">
                            <div className="flex space-x-3">
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors font-medium"
                                >
                                    Update User
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default EditUserOffcanvas;